/**
 * Primitivas visuales del shell.
 *
 * Deliberadamente pocas: boton, tarjeta, chip, cabecera de pagina y estados
 * vacio / carga / error. El renderer de vistas construira las suyas encima de
 * los mismos tokens; lo que aca importa es que el foco, el contraste y el
 * tamano tactil esten resueltos en un solo lugar.
 */

import clsx from 'clsx';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { IconAlert, IconSpinner } from '@/components/shell/icons';

// ---------------------------------------------------------------------------
// Boton
// ---------------------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

const VARIANTES: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-contrast hover:bg-accent-hover shadow-sm',
  secondary: 'border border-line bg-surface text-fg hover:bg-surface-raised hover:border-line-strong',
  ghost: 'text-fg-muted hover:bg-fg/5 hover:text-fg',
  danger: 'bg-danger text-white hover:brightness-110 shadow-sm',
};

const TAMANOS: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-sm',
  md: 'h-10 gap-2 px-4 text-sm',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * El botón no se puede usar AHORA, y el motivo se dice al lado.
   *
   * Es distinto de `disabled` y la diferencia es todo el punto: un
   * `<button disabled>` **no recibe foco y no emite eventos**, así que ni quien
   * navega con teclado ni quien usa lector de pantalla ni quien toca la pantalla
   * tiene forma de enterarse de por qué está apagado; lo único que quedaba era
   * el `title` nativo, que sólo aparece al pasar el mouse por encima de un
   * control muerto y después de un segundo. Con `blocked` el botón sigue siendo
   * un botón: se enfoca, se anuncia como «no disponible» (`aria-disabled`) y al
   * apretarlo cuenta el motivo en vez de ejecutar.
   *
   * **No reemplaza la validación de nadie**: quien lo usa sigue sin ejecutar la
   * acción. Ver `ControlBloqueado` en `views/MotivoBloqueo`, que es lo que
   * conecta este estado con el globo que dice por qué.
   */
  blocked?: boolean;
}

/**
 * `forwardRef` porque hay quien necesita el nodo real: el disparador de un menú
 * tiene que poder recuperar el foco cuando el menú se cierra con Escape, y el
 * botón que se destraba después de guardar tiene que poder recibirlo.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    blocked = false,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={(disabled === true || loading) && !blocked}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex select-none items-center justify-center rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTES[variant],
        TAMANOS[size],
        // `cursor-help` y no `not-allowed`: el botón SÍ hace algo al apretarlo
        // —cuenta por qué no se puede— y prometer con el cursor que no responde
        // es lo que hacía que nadie lo intentara.
        blocked && 'cursor-help opacity-55',
        className,
      )}
      {...props}
    >
      {loading && <IconSpinner className="h-4 w-4" />}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Superficies
// ---------------------------------------------------------------------------
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={clsx('rounded-xl border border-line bg-surface shadow-sm', className)}>{children}</div>
  );
}

type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

const TONOS_BADGE: Record<BadgeTone, string> = {
  neutral: 'bg-surface-raised text-fg-muted border-line',
  accent: 'bg-accent-soft text-accent border-accent/25',
  success: 'bg-success-soft text-success border-success/25',
  warning: 'bg-warning-soft text-warning border-warning/25',
  danger: 'bg-danger-soft text-danger border-danger/25',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
  title,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  /** Texto completo para el tooltip cuando la etiqueta se trunca. */
  title?: string;
}) {
  return (
    <span
      {...(title === undefined ? {} : { title })}
      className={clsx(
        // `whitespace-nowrap`: una pastilla nunca parte su etiqueta en varias
        // líneas (era el caso de «Factura de cliente» en la columna «Tipo»). Si
        // no entra, el hijo se trunca con «…» y el título de arriba dice el resto.
        'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        TONOS_BADGE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Atajo de teclado dibujado como tecla. Se oculta en tactil, donde no aplica. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-line bg-surface-raised px-1.5 py-0.5 font-mono text-2xs text-fg-muted">
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Cabecera de pagina
// ---------------------------------------------------------------------------
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-[1.75rem]">{title}</h1>
        {description !== undefined && <p className="mt-1 max-w-2xl text-fg-muted">{description}</p>}
      </div>
      {actions !== undefined && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Estados
// ---------------------------------------------------------------------------
export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-3 py-16 text-fg-muted">
      <IconSpinner className="h-5 w-5" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line px-6 py-16 text-center">
      <h2 className="text-base font-semibold">{title}</h2>
      {description !== undefined && <p className="mt-2 max-w-md text-sm text-fg-muted">{description}</p>}
      {action !== undefined && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Algo salió mal',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-5 sm:flex-row sm:items-center"
    >
      <IconAlert className="h-6 w-6 shrink-0 text-danger" />
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-fg-muted">{message}</p>
      </div>
      {onRetry !== undefined && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}
